import {
    openSubMenu,
    closeSubMenu,
} from 'root/main/components/navigation/btf/toggleSubMenu';

type AxisToTraverse = 'menubar' | 'child' | 'grandchild';
class BtfNavBar {
    #menubar: HTMLElement;
    #menubarItems: HTMLElement[];
    #menuItemQuerySelector = '[role=menuitem]';

    constructor(el: HTMLElement) {
        if (el) {
            this.#menubar = el;
            this.#menubarItems = Array.from(
                this.#menubar.querySelectorAll<HTMLElement>(this.#menuItemQuerySelector)
            ).filter(menubarItem => {
                return (
                    this.#isMenubarItem(menubarItem) &&
                    menubarItem.getBoundingClientRect().height > 0 &&
                    menubarItem.getBoundingClientRect().width > 0
                );
            });
        }
    }

    #isMenubarItem(menuItem: HTMLElement) {
        return !menuItem.closest('[role=menu]');
    }

    #isParentItem(element: HTMLElement) {
        return element.getAttribute('aria-haspopup') === 'true';
    }

    #getAdjacentGrandChildElements(currentElement: HTMLElement) {
        const adjacentItems = [];
        const childLevelElement = currentElement.closest('[data-navigation-level=child]');
        if (childLevelElement !== null) {
            adjacentItems.push(
                ...Array.from(
                    childLevelElement.querySelectorAll<HTMLElement>(
                        this.#menuItemQuerySelector
                    )
                )
            );
        } else {
            adjacentItems.push(
                ...Array.from(
                    currentElement
                        .closest('[role=menu]')
                        ?.querySelectorAll<HTMLElement>(this.#menuItemQuerySelector) ?? []
                )
            );
        }
        return adjacentItems;
    }

    #getChildLevelItems(element: HTMLElement) {
        return Array.from(
            element
                .closest('[data-navigation-level=parent]')
                ?.querySelectorAll<HTMLElement>(
                    '[data-navigation-level=child] > [role=menuitem]'
                ) ?? []
        );
    }

    #hasChildLevelItems(element: HTMLElement) {
        return element.closest('[data-navigation-level=child]') ? true : false;
    }

    #getTraversalObject(element: HTMLElement, axisToTraverse: AxisToTraverse) {
        if (element) {
            const traversalMap = {
                menubar: {
                    arrayToTraverse: () => {
                        return this.#menubarItems;
                    },
                    elementInAxis: () =>
                        element
                            .closest('[data-navigation-level=parent]')
                            ?.querySelector<HTMLElement>(this.#menuItemQuerySelector),
                },
                child: {
                    arrayToTraverse: () => this.#getChildLevelItems(element),
                    elementInAxis: () =>
                        element
                            .closest('[data-navigation-level=child]')
                            ?.querySelector<HTMLElement>(this.#menuItemQuerySelector),
                },
                grandchild: {
                    arrayToTraverse: () => this.#getAdjacentGrandChildElements(element),
                    elementInAxis: () => element,
                },
            };
            return {
                arrayToTraverse: traversalMap[axisToTraverse].arrayToTraverse(),
                elementInAxis: traversalMap[axisToTraverse].elementInAxis(),
            };
        } else {
            return {};
        }
    }

    #getParentAxis(
        element: HTMLElement,
        axisToTraverse: AxisToTraverse
    ): AxisToTraverse | undefined {
        const axesToTraverse: AxisToTraverse[] = ['grandchild', 'child', 'menubar'];
        let parentAxis: AxisToTraverse | undefined =
            axesToTraverse[axesToTraverse.indexOf(axisToTraverse) + 1];
        if (parentAxis === 'child' && !this.#hasChildLevelItems(element)) {
            parentAxis = this.#getParentAxis(element, parentAxis);
        }
        return parentAxis;
    }

    #traverseNavigationAxis(
        element: HTMLElement,
        direction: 'next' | 'previous' | 'first' | 'last',
        axisToTraverse: AxisToTraverse,
        traverseParentAxisAtLimits = false
    ): boolean {
        const { arrayToTraverse, elementInAxis } = this.#getTraversalObject(
            element,
            axisToTraverse
        );
        if (arrayToTraverse && elementInAxis) {
            const indexOfItem = arrayToTraverse.indexOf(elementInAxis);
            switch (direction) {
                case 'next':
                    if (indexOfItem < arrayToTraverse.length - 1) {
                        arrayToTraverse[indexOfItem + 1].focus();
                        if (axisToTraverse === 'menubar') {
                            closeSubMenu(element);
                        }
                        return true;
                    } else if (axisToTraverse === 'child') {
                        return this.#traverseNavigationAxis(
                            element,
                            direction,
                            'menubar'
                        );
                    } else if (
                        indexOfItem === arrayToTraverse.indexOf(elementInAxis) &&
                        traverseParentAxisAtLimits
                    ) {
                        const parentAxis = this.#getParentAxis(element, axisToTraverse);
                        if (parentAxis) {
                            return this.#traverseNavigationAxis(
                                element,
                                direction,
                                parentAxis
                            );
                        }
                    }
                    break;
                case 'previous':
                    if (indexOfItem > 0) {
                        arrayToTraverse[indexOfItem - 1].focus();
                        if (axisToTraverse === 'menubar') {
                            closeSubMenu(element);
                        }
                        return true;
                    } else {
                        if (axisToTraverse === 'grandchild') {
                            closeSubMenu(element, true);
                        } else if (axisToTraverse === 'child') {
                            return this.#traverseNavigationAxis(
                                element,
                                direction,
                                'menubar'
                            );
                        }
                    }
                    break;
                case 'first':
                    arrayToTraverse[0].focus();
                    return true;
                case 'last':
                    arrayToTraverse[arrayToTraverse.length - 1].focus();
                    return true;
            }
        }
        return false;
    }

    #handleKeydown(event: KeyboardEvent) {
        const currentElement = document.activeElement;
        if (
            currentElement instanceof HTMLElement &&
            currentElement.getAttribute('role') === 'menuitem' &&
            this.#menubar.contains(currentElement)
        ) {
            switch (event.code) {
                case 'ArrowLeft': {
                    event.preventDefault();
                    const axisToTraverse = this.#hasChildLevelItems(currentElement)
                        ? 'child'
                        : 'menubar';
                    this.#traverseNavigationAxis(
                        currentElement,
                        'previous',
                        axisToTraverse
                    );
                    break;
                }
                case 'ArrowRight': {
                    event.preventDefault();
                    const axisToTraverse = this.#hasChildLevelItems(currentElement)
                        ? 'child'
                        : 'menubar';
                    this.#traverseNavigationAxis(currentElement, 'next', axisToTraverse);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    if (!this.#isMenubarItem(currentElement)) {
                        this.#traverseNavigationAxis(
                            currentElement,
                            'previous',
                            'grandchild'
                        );
                    }
                    break;
                }
                case 'ArrowDown':
                    event.preventDefault();
                    if (this.#isParentItem(currentElement)) {
                        openSubMenu(currentElement, true);
                    } else {
                        if (!this.#isMenubarItem(currentElement)) {
                            this.#traverseNavigationAxis(
                                currentElement,
                                'next',
                                'grandchild'
                            );
                        }
                    }
                    break;
                case 'Home': {
                    event.preventDefault();
                    const axisToTraverse = this.#isMenubarItem(currentElement)
                        ? 'menubar'
                        : 'grandchild';
                    this.#traverseNavigationAxis(currentElement, 'first', axisToTraverse);
                    break;
                }
                case 'End': {
                    event.preventDefault();
                    const axisToTraverse = this.#isMenubarItem(currentElement)
                        ? 'menubar'
                        : 'grandchild';
                    this.#traverseNavigationAxis(currentElement, 'last', axisToTraverse);
                    break;
                }
                case 'Space':
                    if (this.#isParentItem(currentElement)) {
                        event.preventDefault();
                        openSubMenu(currentElement, true);
                    }
                    break;
                case 'Enter':
                    if (this.#isParentItem(currentElement)) {
                        event.preventDefault();
                        openSubMenu(currentElement, true);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    closeSubMenu(currentElement, true);
                    break;
                case 'Tab': {
                    const direction = event.shiftKey === true ? 'previous' : 'next';
                    const axisToTraverse = this.#isMenubarItem(currentElement)
                        ? 'menubar'
                        : 'grandchild';
                    const hasTraversalHappened = this.#traverseNavigationAxis(
                        currentElement,
                        direction,
                        axisToTraverse,
                        true
                    );
                    if (hasTraversalHappened) {
                        event.preventDefault();
                    }
                    break;
                }
            }
        }
    }

    listenToKeyDown() {
        document.addEventListener('keydown', e => {
            this.#handleKeydown(e);
        });
    }
}

export default function btfNavBar(btfNavBars: HTMLElement[]) {
    btfNavBars.forEach(btfNavBar => {
        const nb = new BtfNavBar(btfNavBar);
        nb.listenToKeyDown();
    });
}
